<template>
  <div class="step-1-upload">
    <div class="upload-area-container">
      <div
        class="import-dropzone"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
        @click="$refs.fileInput.click()"
        :class="{ 'is-dragging': isDragging, 'has-files': uploadedFiles.length > 0 }"
      >
        <div class="dropzone-icon">
          <img src="~@/assets/images/icons/upload-icon.svg" alt="Upload" />
        </div>
        <h3 class="dropzone-title">Drag & drop Excel files here</h3>
        <p class="dropzone-subtitle">or click to browse your computer</p>

        <div class="dropzone-badges">
          <span class="badge">.xlsx</span>
          <span class="badge">.xls</span>
          <span class="badge">.csv</span>
        </div>

        <p class="dropzone-footer">Multiple files supported</p>

        <input
          type="file"
          ref="fileInput"
          class="file-input"
          multiple
          accept=".xlsx,.xls,.csv"
          @change="handleFileSelect"
        />
      </div>

      <div class="queued-files-section" v-if="uploadedFiles.length > 0">
        <div class="queued-header">QUEUED - {{ uploadedFiles.length }}</div>
        <div class="queued-list">
          <div class="queued-card" v-for="(file, index) in uploadedFiles" :key="file.id">
            <button class="remove-file-btn" @click.stop="removeFile(index)" title="Remove file">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 1L1 9M1 1L9 9" stroke="#9CA3AF" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <div class="file-icon-box">
              <svg width="16" height="20" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.33333 1.66667L14 6.33333V16.6667C14 17.1087 13.8244 17.5326 13.5118 17.8452C13.1993 18.1577 12.7754 18.3333 12.3333 18.3333H3.66667C3.22464 18.3333 2.80072 18.1577 2.48816 17.8452C2.17559 17.5326 2 17.1087 2 16.6667V3.33333C2 2.89131 2.17559 2.46738 2.48816 2.15482C2.80072 1.84226 3.22464 1.66667 3.66667 1.66667H9.33333ZM8.5 2.5H3.66667C3.44565 2.5 3.23369 2.5878 3.07741 2.74408C2.92113 2.90036 2.83333 3.11232 2.83333 3.33333V16.6667C2.83333 16.8877 2.92113 17.0996 3.07741 17.2559C3.23369 17.4122 3.44565 17.5 3.66667 17.5H12.3333C12.5543 17.5 12.7663 17.4122 12.9226 17.2559C13.0789 17.0996 13.1667 16.8877 13.1667 16.6667V7.5H8.5V2.5ZM9.33333 3.67833V6.66667H12.3217L9.33333 3.67833ZM5.33333 9.16667H10.6667V10H5.33333V9.16667ZM5.33333 11.6667H10.6667V12.5H5.33333V11.6667ZM5.33333 14.1667H8.66667V15H5.33333V14.1667Z" fill="#10B981"/>
              </svg>
            </div>
            <div class="file-details">
              <div class="file-name" v-overflow-tooltip>{{ file.name }}</div>
              <div class="file-size">{{ file.size }}</div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { alert } from '@/plugins/notification'

export default {
  name: 'Step1Upload',
  props: {
    uploadedFiles: {
      type: Array,
      required: true
    }
  },
  data () {
    return {
      isDragging: false
    }
  },
  methods: {
    handleDrop (e) {
      this.isDragging = false
      const allowedExtensions = ['xlsx', 'xls', 'csv']
      let invalidCount = 0
      const files = Array.from(e.dataTransfer.files).filter(file => {
        const ext = file.name.split('.').pop().toLowerCase()
        const isAllowed = allowedExtensions.includes(ext)
        if (!isAllowed) {
          invalidCount++
          return false
        }
        const isDuplicate = this.uploadedFiles.some(f => f.name === file.name)
        if (isDuplicate) {
          alert(`File "${file.name}" is already uploaded.`)
        }
        return !isDuplicate
      })

      if (invalidCount > 0) {
        alert('Some files were ignored. Only .xlsx, .xls, and .csv files are permitted.')
      }

      if (files.length > 0) {
        this.$emit('files-selected', files)
      }
    },
    handleFileSelect (e) {
      const allowedExtensions = ['xlsx', 'xls', 'csv']
      let invalidCount = 0
      const files = Array.from(e.target.files).filter(file => {
        const ext = file.name.split('.').pop().toLowerCase()
        const isAllowed = allowedExtensions.includes(ext)
        if (!isAllowed) {
          invalidCount++
          return false
        }
        const isDuplicate = this.uploadedFiles.some(f => f.name === file.name)
        if (isDuplicate) {
          alert(`File "${file.name}" is already uploaded.`)
        }
        return !isDuplicate
      })

      if (invalidCount > 0) {
        alert('Some files were ignored. Only .xlsx, .xls, and .csv files are permitted.')
      }

      if (files.length > 0) {
        this.$emit('files-selected', files)
      }
      this.$refs.fileInput.value = ''
    },
    removeFile (index) {
      this.$emit('remove-file', index)
    }
  }
}
</script>

<style lang="scss" scoped>
.step-1-upload {
  width: 100%;
}

.upload-area-container {
  display: flex;
  gap: 20px;
  align-items: stretch;
  min-height: 300px;
}

.import-dropzone {
  flex: 1;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  padding: 28px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: #ffffff;
  transition: all 0.2s ease;
  min-height: 300px;

  &.has-files {
    flex: 0 0 60%;
  }

  &.is-dragging,
  &:hover {
    border-color: #2f80ff;
    background-color: rgba(47, 128, 255, 0.02);
  }

  .dropzone-icon {
    width: 40px;
    height: 40px;
    background-color: #f3f4f6;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;

    img {
      width: 20px;
      height: 20px;
      opacity: 0.5;
    }
  }

  .dropzone-title {
    font-size: var(--text-base);
    font-weight: 600;
    color: #374151;
    margin: 0 0 4px 0;
  }

  .dropzone-subtitle {
    font-size: var(--text-s);
    color: #9ca3af;
    margin: 0 0 12px 0;
  }

  .dropzone-badges {
    display: flex;
    gap: 6px;
    margin-bottom: 10px;

    .badge {
      background-color: #f3f4f6;
      color: #6b7280;
      font-size: var(--text-xs2);
      padding: 2px 8px;
      border-radius: 4px;
      font-weight: 500;
    }
  }

  .dropzone-footer {
    font-size: var(--text-xs1);
    color: #d1d5db;
    margin: 0;
  }

  .file-input {
    display: none;
  }
}

.queued-files-section {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.queued-header {
  font-size: var(--text-xs1);
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin-bottom: 12px;
}

.queued-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 300px;
  overflow-y: auto;
}

.queued-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  gap: 12px;
  background-color: #ffffff;
  position: relative;
  min-width: 0;

  &:hover {
    .remove-file-btn {
      opacity: 1;
    }
  }
}

.remove-file-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background-color: #f3f4f6;
    path {
      stroke: #ef4444;
    }
  }
}

.file-icon-box {
  width: 32px;
  height: 32px;
  background-color: #ecfdf5;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }
}

.file-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
  min-width: 0;
  padding-right: 20px;
}

.file-name {
  font-size: var(--text-s);
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-size {
  font-size: var(--text-xs2);
  color: #9ca3af;
  margin-bottom: 4px;
}

</style>
